/**
 * «Правила и антиспам» (ТЗ §8, §10.2, §14.2).
 *
 * ЗНАЧЕНИЯ БЕРУТСЯ ТОЛЬКО ИЗ `CabinetBusinessDto`. Пресет отрасли скопирован в строку
 * организации при создании и после этого не читается (AGENTS.md, правило 5): владелец мог
 * поднять окно отмены с 2 до 24 часов, и показать ему пресетные 2 значило бы соврать про
 * его же настройку — причём правдоподобно, потому что цифра выглядела бы настоящей.
 *
 * Каждое поле снабжено пояснением, и это не украшение. Владелец правит здесь защиту
 * от спама и санкции за неявку: «максимум активных записей» без объяснения выглядит как
 * настройка витрины, а на деле это лимит по всей платформе (ТЗ §8), и единица в нём
 * закроет клиенту запись всюду, кроме одного места.
 */

import { useState } from 'react';
import { useAppApi } from '../../app/ApiProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { CtaBar, CtaButton } from '../../ui/Cta';
import { Chip } from '../../ui/controls';
import { ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import { useBusiness } from './useBusiness';
import { Field, Hint } from './profile/fields';
import {
  RULE_FIELDS,
  buildRulesPatch,
  reminderOptions,
  rulesDraftFrom,
  type RuleFieldKey,
  type RuleIssue,
  type RulesDraft,
} from './profile/rules';

export function RulesScreen(props: { businessId: string; onBack: () => void }): JSX.Element {
  const { t } = useI18n();
  const api = useAppApi();
  const { state, business, reload } = useBusiness(props.businessId);

  const [draft, setDraft] = useState<RulesDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  const current = business ? draft ?? rulesDraftFrom(business) : null;
  const result = business && current ? buildRulesPatch(business, current) : null;

  const editNumber = (key: RuleFieldKey, value: string): void => {
    if (!current) return;
    setDraft({ ...current, numbers: { ...current.numbers, [key]: value } });
    setSaved(false);
  };

  const toggleReminder = (hours: number): void => {
    if (!current) return;
    const next = current.reminderLeadHours.includes(hours)
      ? current.reminderLeadHours.filter((value) => value !== hours)
      : [...current.reminderLeadHours, hours];
    setDraft({ ...current, reminderLeadHours: next });
    setSaved(false);
  };

  const save = async (): Promise<void> => {
    if (!business || !result || !result.ok || !result.changed || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.cabinet.updateBusiness(business.id, result.patch);
      // Черновик сбрасывается, чтобы поля показали то, что реально лежит на сервере:
      // набор напоминаний он мог отсортировать иначе, чем нажимал владелец.
      setDraft(null);
      setSaved(true);
      reload();
    } catch (failure) {
      // Отказ бэкенда приходит КЛЮЧОМ i18n и рисуется через ErrorState. Своих текстов
      // «не удалось сохранить» здесь нет: ключ `err.validation` несёт поле и границы,
      // а придуманная фраза их бы потеряла (AGENTS.md, правило 2).
      setSaveError(failure);
    } finally {
      setSaving(false);
    }
  };

  const errors = result && !result.ok ? result.errors : {};
  const options = current ? reminderOptions(current.reminderLeadHours) : [];

  return (
    <Screen>
      <TopBar title={t('setRules')} subtitle={business?.name} onBack={props.onBack} />

      <ScrollArea bottomGap="cta">
        {state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}

        {business && current ? (
          <>
            <div className="px-4 pt-5">
              <Hint>{t('rulesNote')}</Hint>
            </div>

            <div className="px-4 pt-5">
              {RULE_FIELDS.map((field) => (
                <Field
                  key={field.key}
                  numeric
                  label={t(field.labelKey)}
                  value={current.numbers[field.key]}
                  onChange={(value) => editNumber(field.key, value)}
                  note={t(field.noteKey)}
                  error={issueText(errors[field.key], field.min, field.max, t)}
                />
              ))}
            </div>

            <div className="px-4 pt-1">
              <SectionTitle>{t('ruleReminders')}</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {options.map((hours) => (
                  <Chip
                    key={hours}
                    active={current.reminderLeadHours.includes(hours)}
                    onClick={() => toggleReminder(hours)}
                  >
                    {t('ruleReminderHours', { n: hours })}
                  </Chip>
                ))}
              </div>
              {errors.reminderLeadHours ? (
                <div className="mt-2 text-[11.5px] font-semibold text-danger">
                  {t('ruleErrNoReminders')}
                </div>
              ) : null}
              <Hint>{t('ruleRemindersNote')}</Hint>
            </div>

            <div className="px-4 pt-4">
              {saveError ? <ErrorState error={saveError} /> : null}
              {saved ? (
                <div className="text-[12px] font-semibold text-green">{t('setSaved')}</div>
              ) : null}
            </div>
          </>
        ) : null}
      </ScrollArea>

      {business && result ? (
        <CtaBar>
          <CtaButton
            disabled={saving || !result.ok || !result.changed}
            onClick={() => void save()}
          >
            {t('setSave')}
          </CtaButton>
        </CtaBar>
      ) : null}
    </Screen>
  );
}

/**
 * Текст ошибки поля.
 *
 * «Пустое значение не то же самое, что ноль» — отдельная формулировка, а не общее
 * «проверьте ввод»: очищенное поле выглядит для владельца безобидно, а сохранись оно
 * нулём — лимит активных записей отказывал бы каждому клиенту, а cooldown снял бы защиту.
 */
function issueText(
  issue: RuleIssue | undefined,
  min: number,
  max: number,
  t: (key: 'ruleErrEmpty' | 'ruleErrNumber' | 'ruleErrRange', params?: Record<string, number>) => string,
): string | undefined {
  if (issue === undefined) return undefined;
  if (issue === 'empty') return t('ruleErrEmpty');
  if (issue === 'notNumber') return t('ruleErrNumber');
  return t('ruleErrRange', { min, max });
}
